export type PortalRole = "OWNER" | "INTEGRATOR" | "ADMIN" | "VIEWER";

type Claims = Record<string, unknown> | null | undefined;

function normalizeGroupName(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^["']+|["']+$/g, "")
    .trim();
}

export function groupsFromTokenClaims(claims: Claims): string[] {
  const raw = claims?.["cognito:groups"];
  if (Array.isArray(raw)) {
    return raw.map(normalizeGroupName).filter(Boolean);
  }
  if (!raw) return [];

  const text = String(raw).trim();
  if (!text) return [];

  if (text.startsWith("[") && text.endsWith("]")) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map(normalizeGroupName).filter(Boolean);
      }
    } catch {
      // API Gateway-style bracket strings such as [Viewers] are handled below.
    }

    return text
      .slice(1, -1)
      .split(",")
      .map(normalizeGroupName)
      .filter(Boolean);
  }

  return text.split(",").map(normalizeGroupName).filter(Boolean);
}

export function roleFromTokenClaims(claims: Claims): PortalRole | null {
  const groups = new Set(groupsFromTokenClaims(claims));
  if (groups.has("Owners")) return "OWNER";
  if (groups.has("Integrators")) return "INTEGRATOR";
  if (groups.has("TenantAdmins")) return "ADMIN";
  if (groups.has("Viewers")) return "VIEWER";

  const customRole = String(
    claims?.["custom:role"] ?? claims?.role ?? "",
  )
    .trim()
    .toUpperCase();

  // Platform Owner is intentionally group-only. A custom claim may identify
  // downstream roles, but it can never elevate an account to OWNER.
  return ["INTEGRATOR", "ADMIN", "VIEWER"].includes(customRole)
    ? (customRole as PortalRole)
    : null;
}
